import React, { memo, useCallback } from 'react';
import { Accordion } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { TransitionProps } from '@mui/material/transitions';
import { TimeSignal, TimeSignalAttribute } from 'graphql/types';
import { SplitEntry } from '../../types';
import { SplitEntryAccordionContent } from './SplitEntryAccordionContent';
import { SplitEntryAccordionSummary } from './SplitEntryAccordionSummary';

type SplitEntryAccordionProps = {
  note: TimeSignal;
  attributes?: TimeSignalAttribute[];
  entry: SplitEntry;
  expanded: boolean;
  enableSplit: boolean;
  setExpanded: (id: string) => void;
  position: number;
};

const useStyles = makeStyles((theme) => ({
  root: {
    margin: theme.spacing(1, 0),
    borderRadius: theme.spacing(0.5),
    '&::before': {
      backgroundColor: 'transparent'
    }
  }
}));

const transitionProps: TransitionProps = { unmountOnExit: true };
export const SplitEntryAccordion: React.FC<SplitEntryAccordionProps> = memo(
  ({
    note,
    attributes,
    entry,
    expanded,
    enableSplit,
    setExpanded,
    position
  }: SplitEntryAccordionProps) => {
    const classes = useStyles();
    const onChange = useCallback(
      (_, isExpanded) => setExpanded(isExpanded ? entry.id : ''),
      [entry.id, setExpanded]
    );
    return (
      <Accordion
        classes={classes}
        expanded={expanded}
        onChange={onChange}
        data-testid={`accordion_${entry.id}`}
        TransitionProps={transitionProps}
      >
        <SplitEntryAccordionSummary
          note={note}
          entry={entry}
          expanded={expanded}
          enableSplit={enableSplit}
          position={position}
        />
        <SplitEntryAccordionContent
          entry={entry}
          position={position}
          attributes={attributes}
          note={note}
        />
      </Accordion>
    );
  }
);

SplitEntryAccordion.displayName = 'SplitEntryAccordion';
